import React, { useEffect, useMemo, useState } from "react";
import { apiGet, apiPost } from "../../../services/api";
import { useToast } from "../../common/ToastProvider";
import Button from "../../common/Button";
import "./consultants.css";

interface Consultant {
  id: number;
  name: string;
  specialization?: string;
  is_active?: boolean;
}

interface ConsultantService {
  id: number;
  name: string;
  is_active?: boolean;
  procedure_master_id?: number;
  procedure_name?: string;
  procedure_code?: string;
}

interface ProcedureMaster {
  id: number;
  code: string;
  name: string;
  category?: string;
  default_fee?: number;
}

interface ConsultantFee {
  id: number;
  consultant_id: number;
  consultant_name?: string;
  service_id: number;
  service_name?: string;
  fee?: number;
}

interface ConsultantServicesConsoleProps {
  selectedConsultant?: Consultant | null;
}

export default function ConsultantServicesConsole({ selectedConsultant }: ConsultantServicesConsoleProps) {
  const { showToast } = useToast();
  const [services, setServices] = useState<ConsultantService[]>([]);
  const [fees, setFees] = useState<ConsultantFee[]>([]);
  const [consultants, setConsultants] = useState<Consultant[]>([]);
  const [procedures, setProcedures] = useState<ProcedureMaster[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [serviceName, setServiceName] = useState("");
  const [selectedProcedureId, setSelectedProcedureId] = useState<string>("");
  const [editingServiceId, setEditingServiceId] = useState<number | null>(null);

  const [feeConsultantId, setFeeConsultantId] = useState<string>("");
  const [feeServiceId, setFeeServiceId] = useState<string>("");
  const [feeValue, setFeeValue] = useState<string>("");
  const [editingFeeId, setEditingFeeId] = useState<number | null>(null);

  const [showDetails, setShowDetails] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [serviceRes, feeRes, consultantRes, procedureRes] = await Promise.all([
        apiGet("/api/manager/consultant-services"),
        apiGet("/api/manager/consultant-service-fees"),
        apiGet("/api/admin/consultants"),
        apiGet("/api/manager/procedures"),
      ]);
      setServices(Array.isArray(serviceRes) ? (serviceRes as ConsultantService[]) : []);
      setFees(Array.isArray(feeRes) ? (feeRes as ConsultantFee[]) : []);
      const list = Array.isArray(consultantRes) ? (consultantRes as Consultant[]) : [];
      setConsultants(list.filter((c) => c.is_active !== false));
      setProcedures(Array.isArray(procedureRes) ? (procedureRes as ProcedureMaster[]) : []);
    } catch {
      showToast({ message: "Failed to load services data.", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedConsultant?.id) {
      setFeeConsultantId(String(selectedConsultant.id));
      setShowDetails(true); // Auto expand if a consultant is selected
    }
  }, [selectedConsultant?.id]);

  const filteredFees = useMemo(() => {
    if (!feeConsultantId) return fees;
    const id = Number(feeConsultantId);
    return fees.filter((f) => f.consultant_id === id);
  }, [fees, feeConsultantId]);

  const resetServiceForm = () => {
    setServiceName("");
    setSelectedProcedureId("");
    setEditingServiceId(null);
  };

  const resetFeeForm = () => {
    setFeeServiceId("");
    setFeeValue("");
    setEditingFeeId(null);
  };

  const handleSaveService = async () => {
    if (!selectedProcedureId) {
      showToast({ message: "Please select a procedure from the master list.", type: "warning" });
      return;
    }
    const selectedProc = procedures.find(p => p.id === Number(selectedProcedureId));
    if (!selectedProc) {
      showToast({ message: "Invalid procedure selected.", type: "error" });
      return;
    }

    setSaving(true);
    try {
      await apiPost("/api/manager/consultant-services", {
        id: editingServiceId,
        name: selectedProc.name,
        procedure_master_id: selectedProc.id,
      });
      showToast({ message: editingServiceId ? "Service updated successfully." : "Service added successfully.", type: "success" });
      resetServiceForm();
      loadAll();
    } catch {
      showToast({ message: "Failed to save service.", type: "error" });
    } finally {
        setSaving(false);
    }
  };

  const handleDeleteService = async (id: number) => {
    if (!window.confirm("Are you sure you want to deactivate this service? This may affect historical billing records.")) return;
    try {
      await apiPost("/api/manager/consultant-services/delete", { id });
      showToast({ message: "Service deactivated successfully.", type: "success" });
      loadAll();
    } catch {
      showToast({ message: "Failed to deactivate service.", type: "error" });
    }
  };

  const handleSaveFee = async () => {
    if (!feeConsultantId || !feeServiceId) {
      showToast({ message: "Please select both a consultant and a service.", type: "warning" });
      return;
    }

    setSaving(true);
    try {
      await apiPost("/api/manager/consultant-service-fees", {
        id: editingFeeId,
        consultant_id: Number(feeConsultantId),
        service_id: Number(feeServiceId),
        fee: Number(feeValue || 0),
      });
      showToast({ message: editingFeeId ? "Fee mapping updated." : "Fee mapping added.", type: "success" });
      resetFeeForm();
      loadAll();
    } catch {
      showToast({ message: "Failed to save fee mapping.", type: "error" });
    } finally {
        setSaving(false);
    }
  };

  const handleDeleteFee = async (id: number) => {
    if (!window.confirm("Are you sure you want to remove this fee mapping?")) return;
    try {
      await apiPost("/api/manager/consultant-service-fees/delete", { id });
      showToast({ message: "Fee mapping removed.", type: "success" });
      loadAll();
    } catch {
      showToast({ message: "Failed to delete mapping.", type: "error" });
    }
  };

  const inputStyle = { padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.9rem', width: '100%', boxSizing: 'border-box' as const };

  return (
    <div style={{ background: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: 'var(--mod-shadow-sm)', marginBottom: '24px' }}>
      
      {/* Console Header */}
      <div 
        style={{ padding: '20px', background: showDetails ? '#f8fafc' : '#fff', borderBottom: showDetails ? '1px solid #e2e8f0' : 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', transition: 'background 0.2s' }}
        onClick={() => setShowDetails(!showDetails)}
      >
        <div>
           <h3 style={{ margin: 0, fontSize: '1.2rem', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px' }}>
             ⚙️ Services & Fee Configuration
           </h3>
           <p style={{ margin: '4px 0 0 0', fontSize: '0.9rem', color: '#64748b' }}>Manage allowed services and set custom payout rates per consultant.</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <span style={{ background: '#e0f2fe', color: '#0284c7', padding: '4px 12px', borderRadius: '20px', fontSize: '0.8rem', fontWeight: 600 }}>{services.length} Services</span>
            <span style={{ fontSize: '1.2rem', color: '#64748b', transform: showDetails ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>▼</span>
        </div>
      </div>

      {showDetails && (
        <div style={{ padding: '20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', alignItems: 'start' }}>
          
          {/* Service Master Panel */}
          <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <div style={{ padding: '16px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
               <h4 style={{ margin: 0, fontSize: '1rem', color: '#334155' }}>Service Master Directory</h4>
            </div>
            
            <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px', background: '#f8fafc', borderBottom: '1px dashed #cbd5e1' }}>
              <select value={selectedProcedureId} onChange={(e) => setSelectedProcedureId(e.target.value)} style={inputStyle}>
                <option value="">Select procedure from master data...</option>
                {procedures.map((p) => (
                  <option key={p.id} value={p.id}>{p.name} ({p.code})</option>
                ))}
              </select>
              <div style={{ display: 'flex', gap: '8px' }}>
                <Button variant="primary" onClick={handleSaveService} isLoading={saving} style={{ flex: 1 }}>
                  {editingServiceId ? "Update Service" : "Add to Directory"}
                </Button>
                {editingServiceId && <Button variant="outline" onClick={resetServiceForm}>Cancel</Button>}
              </div>
            </div>

            <div style={{ maxHeight: '400px', overflowY: 'auto', padding: '16px' }}>
              {loading && services.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#94a3b8' }}>Loading directory...</div>
              ) : services.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#94a3b8', border: '1px dashed #cbd5e1', borderRadius: '8px' }}>No services configured.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {services.map((s) => (
                    <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', border: '1px solid #e2e8f0', borderRadius: '8px', background: '#fff' }}>
                      <div>
                        <div style={{ fontWeight: 600, color: '#0f172a', fontSize: '0.9rem' }}>{s.name}</div>
                        <div style={{ fontSize: '0.8rem', color: '#64748b' }}>Code: {s.procedure_code || "N/A"}</div>
                      </div>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <Button variant="ghost" size="sm" onClick={() => {
                          setEditingServiceId(s.id);
                          setServiceName(s.name);
                          setSelectedProcedureId(s.procedure_master_id ? String(s.procedure_master_id) : "");
                        }}>Edit</Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteService(s.id)} style={{ color: '#ef4444' }}>X</Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Fee Mapping Panel */}
          <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <div style={{ padding: '16px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
               <h4 style={{ margin: 0, fontSize: '1rem', color: '#334155' }}>Custom Fee Mapping</h4>
            </div>
            
            <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px', background: '#f8fafc', borderBottom: '1px dashed #cbd5e1' }}>
              <select value={feeConsultantId} onChange={(e) => setFeeConsultantId(e.target.value)} style={inputStyle}>
                <option value="">Select consultant...</option>
                {consultants.map((c) => (
                  <option key={c.id} value={c.id}>{c.name} {c.specialization ? `(${c.specialization})` : ""}</option>
                ))}
              </select>
              
              <select value={feeServiceId} onChange={(e) => setFeeServiceId(e.target.value)} style={inputStyle}>
                <option value="">Select service...</option>
                {services.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
              
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#64748b', fontWeight: 600 }}>₹</span>
                <input type="number" min={0} placeholder="Custom Payout Fee" value={feeValue} onChange={(e) => setFeeValue(e.target.value)} style={{ ...inputStyle, paddingLeft: '32px' }} />
              </div>

              <div style={{ display: 'flex', gap: '8px' }}>
                <Button variant="primary" onClick={handleSaveFee} isLoading={saving} style={{ flex: 1 }}>
                  {editingFeeId ? "Update Mapping" : "Add Mapping"}
                </Button>
                {editingFeeId && <Button variant="outline" onClick={resetFeeForm}>Cancel</Button>}
              </div>
            </div>

            <div style={{ maxHeight: '400px', overflowY: 'auto', padding: '16px' }}>
              {loading && fees.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#94a3b8' }}>Loading mapped fees...</div>
              ) : filteredFees.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#94a3b8', border: '1px dashed #cbd5e1', borderRadius: '8px' }}>
                  {feeConsultantId ? "No fees mapped for this consultant." : "Select a consultant to see mappings."}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {filteredFees.map((f) => (
                    <div key={f.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', border: '1px solid #e2e8f0', borderRadius: '8px', background: '#fff' }}>
                      <div style={{ flex: 1, paddingRight: '12px' }}>
                        <div style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 600, marginBottom: '2px' }}>{f.consultant_name || "Unknown"}</div>
                        <div style={{ fontWeight: 600, color: '#0f172a', fontSize: '0.9rem', marginBottom: '4px' }}>{f.service_name || "Unknown"}</div>
                      </div>
                      <div style={{ textAlign: 'right', paddingRight: '16px', borderRight: '1px solid #e2e8f0', marginRight: '16px' }}>
                        <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Payout</div>
                        <div style={{ fontWeight: 700, color: '#16a34a' }}>₹{Number(f.fee || 0).toLocaleString("en-IN")}</div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <Button variant="ghost" size="sm" onClick={() => {
                          setEditingFeeId(f.id);
                          setFeeConsultantId(String(f.consultant_id));
                          setFeeServiceId(String(f.service_id));
                          setFeeValue(String(f.fee ?? ""));
                        }}>Edit</Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteFee(f.id)} style={{ color: '#ef4444' }}>X</Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

        </div>
      )}
    </div>
  );
}
